'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import { signalRSessizKayit } from '@/lib/signalrLogger'
import { getBrowserHubUrl } from '@/lib/pharmacyHubUrl'
import { readChatNotificationPrefs, loadChatNotificationPrefs } from '@/lib/chatNotificationPrefs'
import { getSignalRAccessToken, isSignalRUnauthorizedError } from '@/lib/signalrAccessToken'
import { isSuperAdminPanelPath } from '@/lib/opasSessionRole'
import { refreshTokenSingleton } from '@/lib/tokenRefreshQueue'
import { emitAppToast } from '@/lib/appToast'
import type { InternalMessageDto } from './usePharmacyInternalMessages'

const STORAGE_KEY_NOTIF_PERM = 'opas_chat_notif_perm_asked'

export type ChatOpenTarget = {
  tab: 'internal' | 'network' | 'support'
  networkSubTab?: 'wall' | 'messages' | 'moderation'
}

function playNotificationSound() {
  try {
    const ctx = new AudioContext()
    const now = ctx.currentTime

    const g = ctx.createGain()
    g.connect(ctx.destination)
    g.gain.setValueAtTime(0.18, now)
    g.gain.exponentialRampToValueAtTime(0.01, now + 0.45)

    const o1 = ctx.createOscillator()
    o1.type = 'sine'
    o1.frequency.setValueAtTime(880, now)
    o1.frequency.exponentialRampToValueAtTime(1320, now + 0.08)
    o1.connect(g)
    o1.start(now)
    o1.stop(now + 0.12)

    const o2 = ctx.createOscillator()
    o2.type = 'sine'
    o2.frequency.setValueAtTime(1100, now + 0.13)
    o2.frequency.exponentialRampToValueAtTime(1540, now + 0.2)
    o2.connect(g)
    o2.start(now + 0.13)
    o2.stop(now + 0.45)

    setTimeout(() => ctx.close(), 600)
  } catch {
    // Web Audio API desteklenmiyorsa sessiz devam
  }
}

function showDesktopNotification(title: string, body: string, onClick?: () => void) {
  if (typeof window === 'undefined') return
  if (Notification.permission !== 'granted') return

  const truncatedBody = body.length > 120 ? body.slice(0, 117) + '…' : body
  const n = new Notification(title, {
    body: truncatedBody,
    icon: '/icon',
    tag: 'opas-chat',
    silent: true,
  })

  if (onClick) {
    n.onclick = () => {
      window.focus()
      onClick()
      n.close()
    }
  }

  setTimeout(() => n.close(), 6000)
}

export function requestNotificationPermission() {
  if (typeof window === 'undefined') return
  if (!('Notification' in window)) return
  if (Notification.permission !== 'default') return
  if (sessionStorage.getItem(STORAGE_KEY_NOTIF_PERM)) return

  sessionStorage.setItem(STORAGE_KEY_NOTIF_PERM, '1')
  void Notification.requestPermission()
}

export interface ChatNotificationState {
  unreadCount: number
  resetUnread: () => void
  requestPermission: () => void
  permissionStatus: NotificationPermission | 'unsupported'
}

async function fetchInitialNetworkUnread(): Promise<number> {
  if (typeof window !== 'undefined' && isSuperAdminPanelPath(window.location.pathname)) {
    return 0
  }

  let total = 0
  try {
    const [followRes, threadsRes] = await Promise.all([
      fetch('/api/opas/pharmacy-network/follow-requests/incoming', { credentials: 'include', cache: 'no-store' }),
      fetch('/api/opas/pharmacy-network/dm/threads', { credentials: 'include', cache: 'no-store' }),
    ])
    const followData = followRes.ok ? await followRes.json() : null
    const threadsData = threadsRes.ok ? await threadsRes.json() : null
    if (followData?.items) total += followData.items.length
    if (threadsData?.items) {
      total += (threadsData.items as { isOwnLastMessage?: boolean }[]).filter((t) => !t.isOwnLastMessage).length
    }
  } catch {
    // sessiz
  }
  return total
}

export function useChatNotifications(
  panelOpen: boolean,
  onNotificationClick?: (target?: ChatOpenTarget) => void,
): ChatNotificationState {
  const [unreadCount, setUnreadCount] = useState(0)
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'unsupported'>('default')
  const connRef = useRef<signalR.HubConnection | null>(null)
  const networkConnRef = useRef<signalR.HubConnection | null>(null)
  const panelOpenRef = useRef(panelOpen)
  const initialUnreadLoadedRef = useRef(false)

  useEffect(() => {
    panelOpenRef.current = panelOpen
    if (panelOpen) setUnreadCount(0)
  }, [panelOpen])

  const onClickRef = useRef(onNotificationClick)
  onClickRef.current = onNotificationClick

  useEffect(() => {
    void loadChatNotificationPrefs()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!('Notification' in window)) {
      setPermissionStatus('unsupported')
      return
    }
    setPermissionStatus(Notification.permission)

    const handler = () => setPermissionStatus(Notification.permission)
    navigator.permissions?.query?.({ name: 'notifications' as PermissionName })
      .then((status) => { status.onchange = handler })
      .catch(() => {})

    return () => {}
  }, [])

  useEffect(() => {
    if (initialUnreadLoadedRef.current || panelOpenRef.current) return
    initialUnreadLoadedRef.current = true
    void fetchInitialNetworkUnread().then((n) => {
      if (n > 0 && !panelOpenRef.current) setUnreadCount(n)
    })
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && isSuperAdminPanelPath(window.location.pathname)) {
      return
    }

    let cancelled = false

    async function connect() {
      try {
        const hubUrl = getBrowserHubUrl('/hubs/pharmacy-messages')
        if (!hubUrl) return

        const initial = await getSignalRAccessToken()
        if (!initial) return

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: async () => (await getSignalRAccessToken()) ?? '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .configureLogging(signalRSessizKayit)
          .build()

        connection.on('ReceiveInternalMessage', (payload: unknown) => {
          const msg = payload as InternalMessageDto
          if (!msg?.id || !msg.senderUserId) return

          const myUserId = localStorage.getItem('userId')
          if (myUserId && msg.senderUserId === myUserId) return

          if (!panelOpenRef.current) {
            setUnreadCount((c) => c + 1)
          }

          const senderName = msg.senderUsername?.trim() || msg.senderUserId.slice(0, 10)
          const prefs = readChatNotificationPrefs()

          if (!document.hasFocus() || !panelOpenRef.current) {
            if (prefs.sound) playNotificationSound()
            if (prefs.desktop) {
              showDesktopNotification(senderName, msg.body, () => onClickRef.current?.({ tab: 'internal' }))
            }
          }
        })

        const networkUrl = getBrowserHubUrl('/hubs/pharmacy-network')
        if (networkUrl) {
          const networkConn = new signalR.HubConnectionBuilder()
            .withUrl(networkUrl, {
              accessTokenFactory: async () => (await getSignalRAccessToken()) ?? '',
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalRSessizKayit)
            .build()

          networkConn.on('ReceiveNetworkDm', (payload: unknown) => {
            const msg = payload as { id?: string; senderTenantId?: string; senderUserId?: string; senderUsername?: string; body?: string; isOwn?: boolean }
            if (!msg?.id) return
            const myUserId = localStorage.getItem('userId')
            if (myUserId && msg.senderUserId === myUserId) return
            const myTenantId = localStorage.getItem('tenantId')
            if (myTenantId && msg.senderTenantId === myTenantId && msg.senderUserId === myUserId) return

            if (!panelOpenRef.current) setUnreadCount((c) => c + 1)

            const senderName = msg.senderUsername?.trim() || 'Eczane mesajı'
            const prefs = readChatNotificationPrefs()
            if (!document.hasFocus() || !panelOpenRef.current) {
              if (prefs.sound) playNotificationSound()
              if (prefs.desktop) {
                showDesktopNotification(senderName, msg.body ?? '', () =>
                  onClickRef.current?.({ tab: 'network', networkSubTab: 'messages' }))
              }
            }
          })

          networkConn.on('ReceiveNetworkFollowRequest', (payload: unknown) => {
            const req = payload as {
              requesterPharmacyDisplayName?: string
              requesterUsername?: string
            }
            if (!panelOpenRef.current) setUnreadCount((c) => c + 1)

            const name = req.requesterPharmacyDisplayName?.trim() || req.requesterUsername?.trim() || 'Bir eczane'
            const prefs = readChatNotificationPrefs()
            if (!document.hasFocus() || !panelOpenRef.current) {
              if (prefs.sound) playNotificationSound()
              if (prefs.desktop) {
                showDesktopNotification('Takip isteği', `${name} sizi takip etmek istiyor`, () =>
                  onClickRef.current?.({ tab: 'network', networkSubTab: 'wall' }))
              }
            }
          })

          networkConn.on('SupportTicketResolved', (payload: unknown) => {
            const ticket = payload as {
              userId?: string
              subject?: string
              resolutionMessage?: string
            }
            const myUserId = localStorage.getItem('userId')
            if (myUserId && ticket.userId && ticket.userId !== myUserId) return

            if (typeof document !== 'undefined') {
              document.dispatchEvent(new CustomEvent('opas-support-ticket-resolved'))
            }

            if (!panelOpenRef.current) setUnreadCount((c) => c + 1)

            const body = ticket.resolutionMessage?.trim() || ticket.subject?.trim() || 'Destek talebiniz sonuçlandırıldı.'
            const prefs = readChatNotificationPrefs()
            emitAppToast({
              type: 'info',
              title: 'Destek yanıtı',
              message: body,
              duration: 6000,
            })
            if (!document.hasFocus() || !panelOpenRef.current) {
              if (prefs.sound) playNotificationSound()
              if (prefs.desktop) {
                showDesktopNotification('Destek yanıtı', body, () => onClickRef.current?.({ tab: 'support' }))
              }
            }
          })

          try {
            await networkConn.start()
            if (cancelled) {
              await networkConn.stop()
            } else {
              networkConnRef.current = networkConn
            }
          } catch {
            // network hub optional for notifications
          }
        }

        try {
          await connection.start()
        } catch (e) {
          if (isSignalRUnauthorizedError(e)) {
            await refreshTokenSingleton()
            try {
              await connection.start()
            } catch {
              return
            }
          } else {
            throw e
          }
        }
        if (cancelled) {
          await connection.stop()
          return
        }
        connRef.current = connection
      } catch {
        // bağlantı hatası — sessiz, chat paneli kendi bağlantısını da deniyor
      }
    }

    void connect()

    return () => {
      cancelled = true
      const c = connRef.current
      const nc = networkConnRef.current
      connRef.current = null
      networkConnRef.current = null
      if (c) void c.stop()
      if (nc) void nc.stop()
    }
  }, [])

  const resetUnread = useCallback(() => setUnreadCount(0), [])

  const requestPermission = useCallback(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission !== 'default') return
    void Notification.requestPermission().then((p) => setPermissionStatus(p))
  }, [])

  return { unreadCount, resetUnread, requestPermission, permissionStatus }
}
